<script setup lang="ts">
import type { AppMenuItem, MenuItemBindProps } from '@/types/menu'

defineProps<{
    item: AppMenuItem
    props: MenuItemBindProps
}>()
</script>

<template>
    <button
        v-if="item.root && item.items"
        type="button"
        class="flex items-center justify-between gap-1 cursor-pointer px-4 py-2 font-semibold rounded-lg hover:bg-primary-100 focus-visible:bg-primary-100"
        :class="item.class"
        :data-testid="item.testid"
        v-bind="props.action"
    >
        <span>{{ item.label }}</span>
        <template v-if="item.items">
            <i
                class="pi pi-chevron-down self-center text-xs in-aria-expanded:hidden! md:hidden!"
                aria-hidden="true"
            />
            <i
                class="pi pi-chevron-up self-center text-xs hidden! in-aria-expanded:block! md:hidden!"
                aria-hidden="true"
            />
        </template>
    </button>
    <router-link
        v-else-if="item.root && item.to"
        :to="item.to"
        class="flex items-center justify-between gap-1 cursor-pointer px-4 py-2 font-semibold rounded-lg hover:bg-primary-100 focus-visible:bg-primary-100"
        :class="item.class"
        :data-testid="item.testid"
        v-bind="props.action"
    >
        <span>{{ item.label }}</span>
    </router-link>
    <span
        v-else-if="item.root"
        class="flex items-center justify-between gap-1 px-4 py-2 font-semibold rounded-lg opacity-50 cursor-default"
        :class="item.class"
        :data-testid="item.testid"
        aria-disabled="true"
    >
        <span>{{ item.label }}</span>
    </span>
    <!-- NOTE: Megamenus own column heading elements do not support sublabels. Therefore we need to add them as regular items conditionally for now.-->
    <div v-else-if="item.isColumnHeader" class="flex flex-col gap-1 px-4 py-2" :class="item.class">
        <span class="label-default-semibold text-color">{{ item.label }}</span>
        <span v-if="item.sublabel" class="label-sm-medium text-neutral-600">{{
            item.sublabel
        }}</span>
    </div>
    <router-link
        v-else-if="item.to"
        :to="item.to"
        class="flex flex-col gap-1 cursor-pointer px-4 py-2 font-semibold rounded-lg hover:bg-primary-100 focus-visible:bg-primary-100"
        :class="item.class"
        :data-testid="item.testid"
        v-bind="props.action"
    >
        <span class="label-sm-semibold">{{ item.label }}</span>
        <span v-if="item.sublabel" class="label-sm-medium text-neutral-600">{{
            item.sublabel
        }}</span>
    </router-link>
    <div
        v-else-if="item.url"
        class="flex items-center justify-between gap-1 font-semibold rounded-lg hover:bg-primary-100 focus-visible:bg-primary-100"
    >
        <a
            :class="['flex flex-col px-4 py-2', item.class]"
            :data-testid="item.testid"
            :href="item.url"
            v-bind="props.action"
        >
            <span class="label-sm-semibold">{{ item.label }}</span>
            <span v-if="item.sublabel" class="label-sm-medium text-neutral-600">{{
                item.sublabel
            }}</span>
        </a>
    </div>
    <span
        v-else
        class="flex flex-col gap-1 px-4 py-2 font-semibold rounded-lg opacity-50 cursor-default"
        :class="item.class"
        :data-testid="item.testid"
        aria-disabled="true"
    >
        <span class="label-sm-semibold">{{ item.label }}</span>
        <span v-if="item.sublabel" class="label-sm-medium text-neutral-600">{{
            item.sublabel
        }}</span>
    </span>
</template>
